import React, {useMemo, useRef, useState} from 'react'
import {ContentHotel} from '@findhotel/sapi'

import {useDeviceLayout} from '@vio/atlas/context/deviceLayout'
import {Box} from '@vio/atlas/helpers/Box'
import {Pill} from '@vio/atlas/Pill'
import {Text} from '@vio/atlas/Text'
import {useDispatchTeamEvent} from '@vio/core/src/analytics/components/TeamContext'
import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {Action, Category, Entity} from '@vio/core/src/analytics/types/Events'
import {FormattedMessageWrapper} from '@vio/core/src/localization/components/FormattedMessage'
import {
  DEFAULT_CATEGORY_ID,
  ImageCategory,
  useHotelImagesCategories
} from '@vio/shared/src/accommodation/VerticalGallery/hooks/useHotelImagesCategories'
import {Swiper} from '@vio/shared/src/search/Swiper'

import {AccommodationContentOverlay} from '../AccommodationContentOverlay'
import {GalleryView} from './GalleryView'
import {BackToGalleryIconButton, GalleryCategoriesWrapper} from './styles'

interface Props {
  sourceComponent?: string
  isVisible: boolean
  isGalleryLoading?: boolean
  images: ContentHotel['images']
  hotelDetails?: React.ReactNode
  primaryRoomOffer?: React.ReactNode
  bottomCTA?: React.ReactNode
  className?: string
  onClose: () => void
}
export const HotelGalleryOverlay: React.FC<Props> = ({
  isVisible,
  isGalleryLoading = false,
  images = [],
  hotelDetails,
  primaryRoomOffer,
  bottomCTA,
  className,
  onClose
}) => {
  const {isDesktopXs, isDesktopSm} = useDeviceLayout()
  const imagesContainerRef = useRef<HTMLDivElement>(null)

  const [selectedImage, setSelectedImage] = useState('')
  const {
    categories,
    activeCategory = {} as ImageCategory,
    setActiveCategoryId,
    totalImages,
    activeCategoryPillRef
  } = useHotelImagesCategories(images)
  const dispatchTeamEvent = useDispatchTeamEvent()

  const isLoading = useMemo(
    () => isGalleryLoading || !images.length,
    [isGalleryLoading, images]
  )

  const handleCloseOverlay = () => {
    setSelectedImage('')
    setActiveCategoryId('')
    onClose()
  }

  const galleryEventContext = useMemo(() => {
    return {
      categories: categories?.map(category => ({
        id: category?.id,
        name: category?.name,
        count: category?.count
      }))
    }
  }, [categories])

  const handleCategoryClick = (selectedCategoryId: string) => {
    setActiveCategoryId(selectedCategoryId)
    imagesContainerRef.current?.scrollTo({top: 0, behavior: 'smooth'})

    dispatchTeamEvent(
      trackEvent({
        category: Category.User,
        entity: Entity.ImageCategory,
        action: Action.Clicked,
        payload: {
          ...galleryEventContext,
          selectedCategoryId
        }
      })
    )
  }

  const handleBackToGallery = () => {
    if (selectedImage !== '') {
      setSelectedImage('')
    } else {
      setActiveCategoryId(DEFAULT_CATEGORY_ID)
    }
  }

  return (
    <AccommodationContentOverlay
      isVisible={isVisible}
      onClose={handleCloseOverlay}
      dataId="HotelGalleryOverlay"
      className={className}
      headerCenterContent={
        <Text variant="titleM">
          <FormattedMessageWrapper id="photos" defaultMessage="Photos" />
          {` (${totalImages})`}
        </Text>
      }
      headerStartContent={
        !activeCategory?.default || selectedImage ? (
          <Box
            display="flex"
            gap="s200"
            alignItems="center"
            paddingLeft="s300"
            paddingRight="s300"
            cursor="pointer"
            onClick={handleBackToGallery}
          >
            <BackToGalleryIconButton name="ChevronLeft" />
            <Text variant="labelM">
              <FormattedMessageWrapper
                id="backToGallery"
                defaultMessage="Back to gallery"
              />
            </Text>
          </Box>
        ) : null
      }
      mainContent={
        <>
          <Box hidden={!!selectedImage}>
            {!isLoading && categories.length > 0 && (
              <Swiper
                gap="s300"
                right={0}
                left={0}
                shadowWidth={60}
                buttonSize="md"
              >
                <GalleryCategoriesWrapper>
                  {categories.map(category => (
                    <Pill
                      ref={
                        activeCategory.id === category.id
                          ? activeCategoryPillRef
                          : undefined
                      }
                      className="--gallery-category-item"
                      onClick={() => handleCategoryClick(category.id)}
                      key={category.id}
                      isSelected={activeCategory.id === category.id}
                    >
                      {category.name}
                      {category.count > 0 && ` (${category.count})`}
                    </Pill>
                  ))}
                </GalleryCategoriesWrapper>
              </Swiper>
            )}
          </Box>
          <GalleryView
            ref={imagesContainerRef}
            isLoading={isLoading}
            images={activeCategory.images}
            selectedImage={selectedImage}
            setSelectedImage={setSelectedImage}
            selectedCategory={activeCategory.name}
          />
        </>
      }
      isSidePanelVisible={!isDesktopXs && !isDesktopSm}
      sidePanelContent={hotelDetails}
      primaryRoomOffer={primaryRoomOffer}
      bottomCTA={bottomCTA}
    />
  )
}
